"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { ResponsiveModal, useUrlModalControls } from "@wtc/ui";
import CreateTicketForm from "@/features/tickets/components/CreateTicketForm";

interface CreateTicketButtonProps {
  /** The name of the client/company for the ticket */
  entityName: string;
  /** Optional callback when ticket is successfully created */
  onSuccess?: (ticketId?: string) => void;
  /** Optional callback to refresh data after ticket creation */
  onRefresh?: () => void;
  /** Button variant */
  variant?: "default" | "outline" | "ghost" | "secondary" | "destructive" | "link";
  /** Button size */
  size?: "default" | "sm" | "lg" | "icon";
  /** Custom className for the button */
  className?: string;
  /** Whether to show the button text or just icon */
  showText?: boolean;
}

export const CreateTicketButton: React.FC<CreateTicketButtonProps> = ({
  entityName,
  onSuccess,
  onRefresh,
  variant = "default",
  size = "default",
  className = "",
  showText = true,
}) => {
  const { openModal: openCreateModal } = useUrlModalControls({
    modalId: `create-ticket-${entityName.replace(/\s+/g, "-").toLowerCase()}`,
  });

  const handleSuccess = (ticketId?: string) => {
    // Modal will close automatically when URL changes
    // Call the success callback
    onSuccess?.(ticketId);
    // Refresh data if callback provided
    onRefresh?.();
  };

  return (
    <>
      <Button
        onClick={openCreateModal}
        variant={variant}
        size={size}
        className={`gap-2 ${className}`}
      >
        <Plus className="h-4 w-4" />
        {showText && "Create Ticket"}
      </Button>

      {/* Create Ticket Modal */}
      <ResponsiveModal
        globalModalId={`create-ticket-${entityName.replace(/\s+/g, "-").toLowerCase()}`}
        title={`Create Ticket for ${entityName}`}
        description={`Create a new ticket for ${entityName}`}
        maxWidth="4xl"
        side="bottom"
      >
        <CreateTicketForm onSuccess={handleSuccess} />
      </ResponsiveModal>
    </>
  );
};
